'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Offer, OffersOffer, PaginationType } from '@/services/dataTypes'
import { apiSpecificOffers, apiOfferDetails } from '@/apis/offers'
import { apiExpiredOffers } from '@/apis/user'
import StoreOfferCard from './StoreOfferCard'
import { getMerchantHref, getBaseImageUrl, getProductDetailHref } from '@/constants/hooks'
import { Loader2, Clock } from 'lucide-react'
import dynamic from 'next/dynamic'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'

const ProductCommentModal = dynamic(() => import('./ProductCommentModal'), { ssr: false })

interface Props {
    initialOffers: OffersOffer[]
    companyId: string
    merchantId: string
    awaited_p_id: string
    mer_slug_type: string
    mer_slug: string
    domain: string
    merchantName: string
    merchantLogo: string
    pagination: PaginationType
}

const LazyLoadingOffers = ({ initialOffers, companyId, merchantId, awaited_p_id, mer_slug_type, mer_slug, domain, merchantName, merchantLogo, pagination }: Props) => {
    const [offers, setOffers] = useState<OffersOffer[]>(initialOffers)
    const [expiredOffers, setExpiredOffers] = useState<OffersOffer[]>([])
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const observer = useRef<IntersectionObserver | null>(null)

    const [activeItem, setActiveItem] = useState<OffersOffer | null>(null)
    const fetchingModalRef = useRef(false)

    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()

    useEffect(() => {
        setOffers(initialOffers)
        apiExpiredOffers(merchantId, companyId).then(res => setExpiredOffers(res.data?.offers || [])).catch(() => { })
    }, [initialOffers])

    const loadMore = async () => {
        if (loading || !hasMore) return
        if (page >= pagination?.last_page) { setHasMore(false); return }
        setLoading(true)
        try {
            const res = await apiSpecificOffers(merchantId, companyId, page + 1)
            if (res.data?.offers.length > 0) { setOffers(prev => [...prev, ...res.data.offers]); setPage(p => p + 1) }
            else setHasMore(false)
        } catch { console.error('Error loading offers') }
        finally { setLoading(false) }
    }

    const observerCallback = useCallback((node: any) => {
        if (loading) return
        if (observer.current) observer.current.disconnect()
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loading) loadMore()
        }, { threshold: 0.1 })
        if (node) observer.current.observe(node)
    }, [loading, hasMore])

    // ── Fetch offer by ID for modal ──
    const fetchOfferById = useCallback(async (uniqueId: string) => {
        if (fetchingModalRef.current) return
        fetchingModalRef.current = true
        try {
            const res = await apiOfferDetails(uniqueId, companyId)
            const fetchedOffer: Offer | undefined = res?.data
            if (fetchedOffer) {
                const syntheticItem = {
                    id: (fetchedOffer as any).id,
                    status: 1,
                    offer: fetchedOffer,
                    merchant: (fetchedOffer as any).merchant,
                } as OffersOffer
                setActiveItem(syntheticItem)
            }
        } catch (e) { console.error('Error fetching offer by id:', e) }
        finally { fetchingModalRef.current = false }
    }, [companyId])

    // ── Open modal if URL has ?show=true&p_id=XXX ──
    useEffect(() => {
        const showParam = searchParams.get('show')
        const pIdParam = searchParams.get('p_id') || awaited_p_id
        if (showParam === 'true' && pIdParam) {
            const pool = [...offers, ...expiredOffers]
            const match = pool.find(
                item => String(item?.offer?.unique_id) === pIdParam || String(item?.offer?.id) === pIdParam
            )
            if (match) {
                setActiveItem(match)
            } else if (!activeItem) {
                fetchOfferById(pIdParam)
            }
        }
    }, [searchParams, offers, expiredOffers, awaited_p_id])

    // ── Handle offer card click → open modal + update URL ──
    const handleOfferClick = useCallback((item: OffersOffer) => {
        setActiveItem(item)
        const params = new URLSearchParams(searchParams.toString())
        params.set('show', 'true')
        params.set('p_id', String(item?.offer?.unique_id))
        router.push(`${pathname}?${params.toString()}`, { scroll: false })
    }, [searchParams, pathname, router])

    // ── Close modal + clean URL ──
    const handleCloseModal = useCallback(() => {
        setActiveItem(null)
        const params = new URLSearchParams(searchParams.toString())
        params.delete('show'); params.delete('p_id')
        const query = params.toString()
        router.replace(`${pathname}${query ? `?${query}` : ''}`, { scroll: false })
    }, [searchParams, pathname, router])

    return (
        <div className="space-y-4">
            {offers.map((offer, index) =>
                offer.status === 1 && offer.offer.status === 1 ? (
                    <div key={offer.id} ref={index === offers.length - 1 ? observerCallback : undefined}>
                        <StoreOfferCard
                            product={offer.offer}
                            merchantHref={getMerchantHref(offer.merchant, mer_slug, mer_slug_type)}
                            domain={domain}
                            merchant_name={merchantName}
                            merchant_logo={merchantLogo}
                            companyId={companyId}
                            productDetailUrl={offer.offer?.slug && offer.offer?.is_detail === 1
                                ? getProductDetailHref(offer.merchant, mer_slug_type, offer.offer.slug, offer.offer?.category?.slug)
                                : null}
                            onOfferClick={() => handleOfferClick(offer)}
                        />
                    </div>
                ) : null
            )}

            {loading && (
                <div className="flex justify-center py-6">
                    <Loader2 size={24} className="animate-spin" style={{ color: '#8C5826' }} />
                </div>
            )}

            {expiredOffers?.length > 0 && (
                <div className="mt-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Clock size={15} style={{ color: '#9ca3af' }} />
                        <h2 className="text-sm font-bold" style={{ color: '#9ca3af' }}>
                            Old deals that might still work for {merchantName}
                        </h2>
                    </div>
                    <div className="space-y-4 opacity-60">
                        {expiredOffers.slice(0, 10).map((offer, i) => (
                            <StoreOfferCard key={i}
                                product={offer.offer}
                                merchantHref={getMerchantHref(offer.merchant, mer_slug, mer_slug_type)}
                                domain={domain}
                                merchant_name={merchantName}
                                merchant_logo={merchantLogo}
                                companyId={companyId}
                                onOfferClick={() => handleOfferClick(offer)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Comment Modal */}
            {activeItem && (
                <ProductCommentModal
                    product={activeItem.offer as any}
                    merchantName={activeItem?.merchant?.merchant_name ?? merchantName}
                    merchantLogo={getBaseImageUrl(domain, activeItem?.merchant?.merchant_logo ?? merchantLogo, '')}
                    domain={domain}
                    isOpen={!!activeItem}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    )
}

export default LazyLoadingOffers
